import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "neuracom_consent_v1";
// Time before the full-width banner auto-collapses into a small bottom-right
// pill that no longer intercepts pointer events on the rest of the viewport.
const AUTO_MINIMIZE_AFTER_MS = 6000;

export function CookieConsent() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useImperativeLoad((() => () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch { /* private mode */ }
  })(), []);

  // Auto-collapse the full banner into a pill after a brief idle period so it
  // stops intercepting pointer events on UI in the lower viewport (HoverCards,
  // tooltips, sticky CTAs). The user can still reopen the full banner by
  // clicking the pill, and consent is never recorded without an explicit
  // accept/reject click — GDPR-compliant.
  useEffect(() => {
    if (!visible || minimized) return undefined;
    const id = window.setTimeout(() => setMinimized(true), AUTO_MINIMIZE_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [visible, minimized]);

  const save = async (analytics, marketing) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ analytics, marketing, at: new Date().toISOString() })
      );
    } catch { /* ignore */ }
    setVisible(false);
    // Best-effort: also persist server-side if user is authenticated
    api.post("/user/consent", { analytics, marketing }).catch(() => {});
  };

  if (!visible) return null;

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-[9999] inline-flex items-center gap-2 rounded-full border border-[#9ACD32]/40 bg-[#05073B]/95 px-4 py-2 text-xs font-semibold text-[#9ACD32] shadow-lg backdrop-blur-md transition-transform hover:scale-105 hover:bg-[#05073B]"
        data-testid="cookie-consent-pill"
        aria-label={t("cookies.pill_aria")}
      >
        <Cookie className="h-4 w-4" />
        {t("cookies.pill_label")}
      </button>
    );
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] border-t border-[#9ACD32]/30 bg-[#05073B]/95 backdrop-blur-md"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label={t("cookies.banner_aria")}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:gap-6 lg:py-4">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-[#9ACD32]" />
          <div className="text-sm leading-relaxed text-slate-200">
            <p>
              <span className="font-bold text-white">{t("cookies.banner_title")}</span>{" "}
              {t("cookies.banner_text")}{" "}
              <Link to="/cookies" className="underline hover:text-[#9ACD32]" data-testid="cookie-banner-policy-link">
                {t("cookies.banner_learn_more")}
              </Link>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto lg:shrink-0">
          <Button
            onClick={() => save(false, false)}
            variant="outline"
            className="rounded-full border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
            data-testid="cookie-reject-btn"
          >
            {t("cookies.reject_all")}
          </Button>
          <Button
            onClick={() => save(true, true)}
            className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="cookie-accept-btn"
          >
            {t("cookies.accept_all")}
          </Button>
        </div>
      </div>
    </div>
  );
}
